/**
 * Resolve a display image / family label from sysInfo Manufacturer + Model.
 * Prefers product-line silhouettes over per-SKU photos (fleet has many Lenovo MTMs).
 */

const normalize = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[,_]+/g, " ")
    .replace(/\s+/g, " ")

const brandOf = (manufacturer) => {
  const value = normalize(manufacturer)
  if (!value) return "unknown"
  if (value.includes("dell")) return "dell"
  if (value.includes("lenovo") || value.includes("ibm")) return "lenovo"
  if (value.includes("hp") || value.includes("hewlett")) return "hp"
  if (value.includes("apple")) return "apple"
  if (value.includes("microsoft")) return "microsoft"
  if (value.includes("asus")) return "asus"
  if (value.includes("acer")) return "acer"
  if (value.includes("toshiba") || value.includes("dynabook")) return "toshiba"
  if (value.includes("samsung")) return "samsung"
  if (value.includes("huawei") || value.includes("honor")) return "huawei"
  if (value.includes("msi")) return "msi"
  if (value.includes("vmware") || value.includes("virtual") || value.includes("qemu") || value.includes("xen")) {
    return "virtual"
  }
  return "unknown"
}

/** Lenovo machine-type prefixes → ThinkPad family (common in this fleet). */
const LENOVO_THINKPAD_PREFIX = /^(20|21|22)[a-z0-9]/i
const LENOVO_IDEAPAD_PREFIX = /^(81|82|83)[a-z0-9]/i

const familyOf = (brand, model) => {
  const m = normalize(model)
  if (!m) {
    if (brand === "apple") return "macbook"
    if (brand === "virtual") return "virtual"
    return brand === "unknown" ? "laptop" : "laptop"
  }

  if (/virtual machine|hyper-v|vmware|virtualbox/.test(m)) return "virtual"
  if (/precision tower|optiplex|thinkcentre|prodesk|elitedesk|tower|desktop|sff|mini pc/.test(m)) {
    return "desktop"
  }
  if (/server|poweredge|proliant|thinksystem|r[0-9]{3}/.test(m)) return "server"

  if (brand === "dell") {
    if (/latitude/.test(m)) return "dell-latitude"
    if (/inspiron/.test(m)) return "dell-inspiron"
    if (/vostro/.test(m)) return "dell-vostro"
    if (/xps/.test(m)) return "dell-xps"
    if (/precision/.test(m)) return "dell-precision"
    return "dell-latitude"
  }

  if (brand === "lenovo") {
    if (/thinkpad|think book|thinkbook/.test(m)) return "lenovo-thinkpad"
    if (/ideapad|yoga|legion/.test(m)) return "lenovo-ideapad"
    // Machine type codes used by fleet (e.g. 20L6S42005 → ThinkPad)
    const token = m.split(/[\s/]/)[0] || ""
    if (LENOVO_THINKPAD_PREFIX.test(token)) return "lenovo-thinkpad"
    if (LENOVO_IDEAPAD_PREFIX.test(token)) return "lenovo-ideapad"
    return "lenovo-thinkpad"
  }

  if (brand === "hp") {
    if (/elitebook|zbook|probook|pavilion|envy|omen|spectre/.test(m)) return "hp-elitebook"
    return "hp-elitebook"
  }

  if (brand === "apple") {
    if (/macbookair|macbook air|mba/.test(m)) return "macbook-air"
    if (/imac|mac mini|macstudio|mac pro/.test(m)) return "mac-desktop"
    return "macbook"
  }

  if (brand === "microsoft") {
    if (/surface/.test(m)) return "surface"
    if (/virtual/.test(m)) return "virtual"
    return "surface"
  }

  if (brand === "asus") {
    if (/zenbook/.test(m)) return "asus-zenbook"
    return "asus-vivobook"
  }

  if (brand === "acer") {
    if (/aspire|swift|travelmate/.test(m)) return "acer-aspire"
    return "acer-aspire"
  }

  return "laptop"
}

const FAMILY_LABELS = {
  "dell-latitude": "Dell Latitude",
  "dell-inspiron": "Dell Inspiron",
  "dell-vostro": "Dell Vostro",
  "dell-xps": "Dell XPS",
  "dell-precision": "Dell Precision",
  "lenovo-thinkpad": "Lenovo ThinkPad",
  "lenovo-ideapad": "Lenovo IdeaPad",
  "hp-elitebook": "HP EliteBook",
  macbook: "MacBook",
  "macbook-air": "MacBook Air",
  "mac-desktop": "Mac desktop",
  surface: "Microsoft Surface",
  "asus-vivobook": "ASUS Vivobook",
  "asus-zenbook": "ASUS Zenbook",
  "acer-aspire": "Acer Aspire",
  laptop: "Laptop",
  desktop: "Desktop",
  server: "Server",
  virtual: "Virtual machine",
}

const FAMILY_COLORS = {
  "dell-latitude": "#007DB8",
  "dell-inspiron": "#007DB8",
  "dell-vostro": "#007DB8",
  "dell-xps": "#007DB8",
  "dell-precision": "#007DB8",
  "lenovo-thinkpad": "#E2231A",
  "lenovo-ideapad": "#E2231A",
  "hp-elitebook": "#0096D6",
  macbook: "#64748b",
  "macbook-air": "#64748b",
  "mac-desktop": "#64748b",
  surface: "#107C10",
  "asus-vivobook": "#0f172a",
  "asus-zenbook": "#0f172a",
  "acer-aspire": "#83B81A",
  laptop: "#2563eb",
  desktop: "#475569",
  server: "#7c3aed",
  virtual: "#94a3b8",
}

export const readManufacturerModel = (device = {}) => {
  const sys = device.sysInfo || device || {}
  const manufacturer =
    sys.Manufacturer ||
    sys.manufacturer ||
    sys["System Manufacturer"] ||
    sys.SystemManufacturer ||
    ""
  const model =
    sys.Model ||
    sys.model ||
    sys["System Model"] ||
    sys.SystemModel ||
    sys["Product Name"] ||
    ""
  return { manufacturer: String(manufacturer || "").trim(), model: String(model || "").trim() }
}

export const resolveDeviceModelImage = (device = {}) => {
  const { manufacturer, model } = readManufacturerModel(device)
  const brand = brandOf(manufacturer)
  let family = familyOf(brand, model)

  // Prefer battery as a soft laptop hint when model is empty
  if (family === "laptop" && !model) {
    const battery = device.battery
    if (Array.isArray(battery) && battery.length) family = "laptop"
  }

  if (device.deviceCategory === "server" || /server/i.test(device.osName || "")) {
    family = "server"
  }

  return {
    manufacturer,
    model,
    brand,
    family,
    label: FAMILY_LABELS[family] || "Device",
    color: FAMILY_COLORS[family] || "#2563eb",
    displayModel: model || FAMILY_LABELS[family] || "Unknown model",
  }
}

export const DEVICE_MODEL_FAMILIES = Object.keys(FAMILY_LABELS)
